/**
 * Canonical Tool Renderer Registry
 * 
 * Registry for tool-specific rendering in CanonicalToolCard.
 * Enables extensible rendering for different tool types (web_search, doc_write, etc.)
 * 
 * Similar to CanonicalCardRegistry but specifically for tools.
 */

import React from 'react';
import { ToolExecutionState } from '../../types/CanonicalMessage';
import { createFileLogger } from '../../../../../utils/logger';

const log = createFileLogger('CANONICAL_TOOL_RENDERER_REGISTRY', { enabled: false });

/**
 * Context provided to tool renderers
 */
export interface CanonicalToolRenderContext {
  toolId: string;
  /** Anthropic/provider block ID (toolu_*, call_*, …). Equals ctx.toolCallId inside the tool handler.
   *  Use this as the correlationId for waitForHumanInput — NOT toolId, which is a frontend-generated
   *  canonical ID (tc_timestamp_counter) that differs from the provider block ID. */
  providerToolCallId?: string;
  toolName: string;
  input: Record<string, any>;
  execution: ToolExecutionState;
  threadId: string;
  entityName?: string;
  resolvedEntity?: any; // Entity object if resolved
  getEntityIcon?: (entity: any) => React.ReactNode; // Function to get entity icon
  /** Optional space-scoped data for tool UIs (e.g. mail integrations). */
  spaceContext?: {
    mailIntegrations?: Array<{ id: string; accountEmail: string }>;
  };
}

/**
 * Tool renderer interface
 * Different tools can customize their rendering for each state
 *
 * --- Flag alignment rules (apply consistently across sibling/paired tools) ---
 *
 * useEntityIcon: set `true` whenever `shouldResolveEntity: true` and the tool
 *   reads/writes a specific, already-existing entity — the entity's real icon
 *   is strictly more informative than a generic tool icon (e.g. doc/slide/
 *   diagram/memory/prompt/skill writes). Leave `false` (default) for tools
 *   that act on a class of entities rather than one resolved instance.
 *
 * defaultExpanded: align paired tools that perform the same kind of operation
 *   (e.g. all "*_create" renderers, all search/find renderers) so switching
 *   between siblings doesn't feel inconsistent. Default to `true` unless the
 *   result is routinely long/verbose (e.g. raw content reads).
 *
 * isNavigable: default to `true` whenever the tool resolves to a single
 *   entity that can be opened (matches `shouldResolveEntity: true` +
 *   `navigableEntityId`/documentId being available) — there's no reason to
 *   suppress navigation just because the tool is a "get" vs a "read". Only
 *   set `false` for tools with no sensible navigation target.
 */
export interface CanonicalToolRenderer {
  // --- Header Customization ---
  
  // Icon to display in header (React component or function returning ReactNode)
  // If not provided, uses PencilSimple as fallback
  icon?: React.ComponentType<any> | ((context: CanonicalToolRenderContext) => React.ReactNode);
  
  // Display name for the tool (used when entity context is not available)
  // Can be a string or function that generates the name dynamically
  displayName?: string | ((context: CanonicalToolRenderContext) => string);
  
  // Action verb for the tool (e.g., "Reading"/"Read", "Writing"/"Wrote")
  // Returns present tense when isExecuting=true, past tense when false
  getVerb?: (isExecuting: boolean, context: CanonicalToolRenderContext) => string;
  
  // Custom header message (appears after entity name in header)
  // Allows dynamic messages like "Found 5 sources", "3 results", etc.
  // Return null to show no message and skip execution result fallback; undefined to use default fallback.
  getHeaderMessage?: (context: CanonicalToolRenderContext) => string | null | undefined;

  // Custom header meta — short text pinned to the RIGHT edge of the header's
  // top row, inline with the verb/entity name (e.g. an elapsed timer). Unlike
  // getHeaderMessage (which stacks on its own line below the verb), this sits
  // on the SAME row, in the same slot the collapse chevron would occupy.
  // Return null/undefined to show nothing.
  getHeaderMeta?: (context: CanonicalToolRenderContext) => string | null | undefined;
  
  // Whether to resolve entity (fetch entity name/icon from ID)
  // false = skip entity resolution, use displayName instead
  // Default: true
  shouldResolveEntity?: boolean;
  
  // Whether to show the entity icon INSTEAD of the tool icon in the header
  // true = use entity icon (e.g., doc writes - show what's being written to)
  // false = use tool icon (e.g., doc reads - show the action)
  // Default: false (show tool icon)
  useEntityIcon?: boolean;
  
  // Whether clicking on the completed tool card opens/navigates to the entity
  // Can be boolean or function for dynamic behavior
  // Default: true if execution has documentId
  isNavigable?: boolean | ((context: CanonicalToolRenderContext) => boolean);

  // Optional: extract the entity/document ID to open when the card is clicked.
  // When provided, the card is automatically navigable on completion (no need to set isNavigable).
  // Use this instead of custom renderers when you just want click-to-open behavior.
  // Return null/undefined to suppress navigation for a specific execution.
  navigableEntityId?: (context: CanonicalToolRenderContext) => string | null | undefined;

  /**
   * Called after card-click navigation succeeds (window opened or focused).
   * `wasAlreadyOpen`: true = focusWindow was called; false = openEntity created a new window.
   *
   * USE THIS for post-navigation side effects in non-ProseMirror apps (TasksApp, AutomationsApp,
   * ContactsApp, etc.) where there are no DOM node IDs to target with scrollToNode.
   *
   * Pattern:
   *   1. Set `navigableEntityId` to enable Tier 1 navigation (open/focus the window).
   *   2. In `onNavigated`, dispatch `river:navigateToItem` with `{ windowId, itemId }`.
   *   3. The target app listens to `river:navigateToItem`, filters by `windowId`, and handles
   *      item-level navigation in its own way (expand a task row, scroll to a card, etc.).
   *
   * Example — TaskWriteRenderer:
   *   navigableEntityId: (ctx) => ctx.execution.result?.raw?.data?.entityId ?? null,
   *   onNavigated: (ctx, { windowId, wasAlreadyOpen }) => {
   *     if (!wasAlreadyOpen) return; // entity prop handles new-window navigation
   *     const itemId = ctx.execution.result?.raw?.data?.entityId;
   *     if (itemId) window.dispatchEvent(new CustomEvent('river:navigateToItem',
   *       { detail: { windowId, itemId } }));
   *   }
   *
   * For ProseMirror apps (DocsApp, NotesApp), prefer returning nodeIds via the result data
   * so that the existing scrollToNode DOM path handles scrolling automatically — no onNavigated
   * needed.
   *
   * When wasAlreadyOpen=false: the entity prop passed to createWindow usually triggers the
   * app's own navigation effect, so onNavigated can often be a no-op for new windows.
   */
  onNavigated?: (
    context: CanonicalToolRenderContext,
    result: { windowId: string; entityId: string; wasAlreadyOpen: boolean }
  ) => void;
  
  // Fallback name to use when entity resolution fails
  // Shows when no entity name is found in input or store
  // Can be a string or function for dynamic behavior
  // Default: "Entity" (if not specified)
  fallbackEntityName?: string | ((context: CanonicalToolRenderContext) => string);
  
  // --- Layout Customization ---
  
  // Compact/inline rendering (alternative to full card view)
  // Used for tools that can be displayed more concisely (e.g., doc_read)
  // If provided, this overrides the entire card rendering when completed
  renderCompact?: (context: CanonicalToolRenderContext) => React.ReactNode;

  // When true, renderCompact is used for ALL execution states (queued, executing, completed, error),
  // bypassing the full tool-use-card shell entirely. renderCompact must handle all states itself.
  inlineOnly?: boolean;
  
  // Whether this tool should be expanded by default when completed
  // true = expanded (show results), false = collapsed (click to expand)
  // Default: true for most tools, false for verbose read operations
  defaultExpanded?: boolean;

  // When true, this card is ALWAYS expanded and never shows the collapse
  // chevron at all — not "defaults to expanded but user can collapse it",
  // genuinely no toggle affordance. Use for cards where the body IS the
  // result (e.g. script_run's narration notes) and hiding it would leave
  // nothing behind but the bare verb.
  alwaysExpanded?: boolean;
  
  // --- State Rendering ---
  
  // Render during execution (optional - shows progress/live updates)
  renderExecuting?: (context: CanonicalToolRenderContext) => React.ReactNode;
  
  // Render completed state (optional when silent: true)
  renderCompleted?: (context: CanonicalToolRenderContext) => React.ReactNode;
  
  // Render error state (optional - uses default if not provided)
  renderError?: (context: CanonicalToolRenderContext) => React.ReactNode;
  
  // --- Behavior Flags ---

  // When true, the tool card renders nothing at all (no UI shown during or after execution).
  // Use for internal plumbing tools the user should never see (e.g. search_tools).
  silent?: boolean;

  // Returns true when the tool completed successfully but produced no meaningful results.
  // Causes the card to render in a minimal inline style (no expand button, no body, muted header).
  // Use for tools whose empty result is normal and not worth visual emphasis.
  isEmptyResult?: (context: CanonicalToolRenderContext) => boolean;

  // When true (or when the predicate returns true for the current execution),
  // this tool's execution phase is a Human-in-the-Loop (HITL) wait — the tool
  // is blocked in waitForHumanInput(), not actively computing.
  //
  // CanonicalToolCard bypasses the standard executing visual:
  //   - No lighting-effect animation
  //   - No standard header or verb
  //   - renderExecuting() is rendered directly inside a static .hitl-pending card wrapper
  //
  // Always pair with a renderExecuting() that shows the approval UI (confirm card with
  // Send/Cancel or equivalent). Use providerToolCallId (not toolId) as the correlationId.
  //
  // Most HITL tools (propose_plan, ask_user, send_sms, ...) are ALWAYS blocked
  // for their entire execution — use `true`. A tool that only SOMETIMES blocks
  // on human input (e.g. apollo_search only pauses when its estimated cost
  // crosses ctx.requestSpendApproval's threshold) should pass a predicate that
  // inspects `context.execution.progress` to report the *current* call's real
  // state — see isSpendApprovalPending() in SpendApprovalCard.tsx. Use
  // resolveHumanInputPending() (below) rather than reading this field directly.
  //
  // See: tool-runtime/src/agent/humanInputRegistry.ts
  // See: CREATING_TOOLS.md — Pattern 4 (HITL tools)
  humanInputPending?: boolean | ((context: CanonicalToolRenderContext) => boolean);

  // Whether this renderer supports streaming
  supportsStreaming?: boolean;

  /**
   * Overrides the name-derived default grouping tier/bucket used by
   * mergeToolCallRuns.ts (see deriveActivity() in rendererUtils.ts) when
   * collapsing consecutive tool calls in chat. Most tools don't need this —
   * the tier is inferred correctly from the tool's name suffix. Only set this
   * for the small set of exceptions whose name doesn't match their actual
   * shape (e.g. `calendar_read` returns many events, not one) or that must
   * never be grouped (HITL/interactive tools).
   */
  activity?: {
    tier: 'exploration' | 'action' | 'exempt';
    /** Bucket noun for the "Explored N {noun}s" summary. Only meaningful for tier: 'exploration'. */
    noun?: 'item' | 'search';
  };
}

interface FallbackRendererEntry {
  predicate: (toolName: string) => boolean;
  renderer: CanonicalToolRenderer;
}

/**
 * Registry for canonical tool renderers
 */
class CanonicalToolRendererRegistry {
  private renderers = new Map<string, CanonicalToolRenderer>();
  private fallbacks: FallbackRendererEntry[] = [];

  /**
   * Register a tool renderer by exact tool name.
   */
  register(toolName: string, renderer: CanonicalToolRenderer): void {
    if (this.renderers.has(toolName)) {
      log.warn(`[CanonicalToolRendererRegistry] Overwriting renderer for: ${toolName}`);
    }
    this.renderers.set(toolName, renderer);
    log.log(`[CanonicalToolRendererRegistry] Registered renderer for: ${toolName}`);
  }

  /**
   * Register a fallback renderer that applies to tool names matching a predicate.
   * Fallbacks are checked in registration order when no exact-name match exists.
   */
  registerFallback(predicate: (toolName: string) => boolean, renderer: CanonicalToolRenderer): void {
    this.fallbacks.push({ predicate, renderer });
  }

  /**
   * Get renderer for a tool — exact match first, then first matching fallback.
   */
  getRenderer(toolName: string): CanonicalToolRenderer | null {
    if (this.renderers.has(toolName)) return this.renderers.get(toolName)!;
    return this.fallbacks.find(f => f.predicate(toolName))?.renderer ?? null;
  }

  /**
   * Check if a renderer exists for the given tool name.
   */
  hasRenderer(toolName: string): boolean {
    if (this.renderers.has(toolName)) return true;
    return this.fallbacks.some(f => f.predicate(toolName));
  }
  
  /**
   * Render executing state for a tool.
   * Uses getRenderer() so fallback-registered tools (e.g. Composio) are honored.
   */
  renderExecuting(context: CanonicalToolRenderContext): React.ReactNode {
    const renderer = this.getRenderer(context.toolName);
    if (!renderer?.renderExecuting) return null;
    try {
      return renderer.renderExecuting(context);
    } catch (error) {
      log.error(`[CanonicalToolRendererRegistry] Error in renderExecuting for ${context.toolName}:`, error);
      return null;
    }
  }

  /**
   * Render completed state for a tool.
   * Uses getRenderer() so fallback-registered tools (e.g. Composio) are honored.
   */
  renderCompleted(context: CanonicalToolRenderContext): React.ReactNode {
    const renderer = this.getRenderer(context.toolName);
    if (!renderer) return null;
    try {
      return renderer.renderCompleted?.(context) ?? null;
    } catch (error) {
      log.error(`[CanonicalToolRendererRegistry] Error in renderCompleted for ${context.toolName}:`, error);
      return this.renderError(context, error);
    }
  }

  /**
   * Render error state for a tool.
   * Uses getRenderer() so fallback-registered tools (e.g. Composio) are honored.
   */
  renderError(context: CanonicalToolRenderContext, error?: any): React.ReactNode {
    const renderer = this.getRenderer(context.toolName);
    if (renderer?.renderError) {
      try {
        return renderer.renderError(context);
      } catch (err) {
        log.error(`[CanonicalToolRendererRegistry] Error in renderError for ${context.toolName}:`, err);
      }
    }
    return null;
  }
  
  /**
   * Get all registered tool names
   */
  getRegisteredTools(): string[] {
    return Array.from(this.renderers.keys());
  }
  
  /**
   * Clear all renderers and fallbacks (for testing).
   */
  clear(): void {
    this.renderers.clear();
    this.fallbacks = [];
  }
}

// Singleton instance
export const canonicalToolRendererRegistry = new CanonicalToolRendererRegistry();

/**
 * Resolve a renderer's `humanInputPending` flag against a specific execution's
 * context — the single place that understands both the static-boolean form
 * (propose_plan, ask_user, send_sms, ...) and the conditional-predicate form
 * (apollo_search, apollo_enrich, ...). Every consumer of `humanInputPending`
 * (CanonicalToolCard's HITL bypass, conversationStore's abandoned-card
 * finalizer) should call this instead of reading `renderer.humanInputPending`
 * directly, so a future conditional HITL tool gets correct behavior everywhere
 * for free.
 */
export function resolveHumanInputPending(
  renderer: CanonicalToolRenderer | null | undefined,
  context: CanonicalToolRenderContext,
): boolean {
  if (!renderer?.humanInputPending) return false;
  if (typeof renderer.humanInputPending === 'function') {
    try {
      return renderer.humanInputPending(context);
    } catch (error) {
      log.error(`[CanonicalToolRendererRegistry] Error in humanInputPending predicate for ${context.toolName}:`, error);
      return false;
    }
  }
  return renderer.humanInputPending === true;
}


